import { useEffect, useState } from 'react';

interface TranscriptEntry {
  id: string;
  timestamp: string;
  text: string;
  speaker?: string;
}

interface AISummaryPanelProps {
  transcript: TranscriptEntry[];
  isRecording: boolean;
}

interface SummaryData {
  overview: string;
  keyPoints: string[];
  actionItems: string[];
  highlights: string[];
}

const AISummaryPanel = ({ transcript, isRecording }: AISummaryPanelProps) => {
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (transcript.length > 0 && !isRecording) {
      generateSummary();
    }
  }, [transcript, isRecording]);

  const generateSummary = () => {
    setIsGenerating(true);
    
    setTimeout(() => {
      setSummary({
        overview: "This meeting covered quarterly business results, marketing performance, and resource allocation. The team discussed revenue growth, customer feedback, and upcoming product launches with specific action items assigned.",
        keyPoints: [
          "Revenue increased by 23% compared to last quarter",
          "Marketing campaign performed exceptionally well in European market",
          "Customer feedback has been overwhelmingly positive about new features",
          "Budget approval is pending from finance department",
          "Next milestone set for end of Q2"
        ],
        actionItems: [
          "Schedule follow-up meeting with design team next week",
          "Allocate more resources to product development team",
          "Consider expanding customer support hours",
          "Review roadmap for upcoming product launch"
        ],
        highlights: [
          "23% revenue increase - significant growth achievement",
          "European market success - potential for expansion",
          "Positive customer feedback - validates product direction",
          "Q2 milestone approaching - timeline on track"
        ]
      });
      setIsGenerating(false);
    }, 1500);
  };

  if (transcript.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl flex items-center justify-center mb-4">
          <i className="ri-ai-generate text-3xl text-purple-500"></i>
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">No Summary Yet</h3>
        <p className="text-sm text-slate-500 max-w-md">
          Start recording to generate an AI-powered summary with key points, action items, and highlights.
        </p>
      </div>
    );
  }

  if (isRecording) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-blue-100 rounded-2xl flex items-center justify-center mb-4 animate-pulse">
          <i className="ri-mic-fill text-3xl text-teal-500"></i>
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Recording in Progress</h3>
        <p className="text-sm text-slate-500 max-w-md">
          Stop recording to generate your AI summary
        </p>
      </div>
    );
  }

  if (isGenerating) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl flex items-center justify-center mb-4">
          <i className="ri-loader-4-line text-3xl text-purple-500 animate-spin"></i>
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Generating Summary...</h3>
        <p className="text-sm text-slate-500 max-w-md">
          AI is analyzing your transcript
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
            <i className="ri-file-text-fill text-white text-sm"></i>
          </div>
          <h3 className="text-lg font-semibold text-slate-900">Overview</h3>
        </div>
        <p className="text-slate-700 leading-relaxed bg-slate-50 rounded-lg p-4">
          {summary?.overview}
        </p>
      </div>

      {/* Key Points */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-green-500 rounded-lg flex items-center justify-center">
            <i className="ri-lightbulb-fill text-white text-sm"></i>
          </div>
          <h3 className="text-lg font-semibold text-slate-900">Key Points</h3>
        </div>
        <ul className="space-y-2">
          {summary?.keyPoints.map((point, index) => (
            <li key={index} className="flex items-start gap-3 bg-slate-50 rounded-lg p-3">
              <div className="w-6 h-6 bg-teal-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold mt-0.5">
                {index + 1}
              </div>
              <span className="text-slate-700 text-sm leading-relaxed">{point}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Action Items */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
            <i className="ri-checkbox-circle-fill text-white text-sm"></i>
          </div>
          <h3 className="text-lg font-semibold text-slate-900">Action Items</h3>
        </div>
        <ul className="space-y-2">
          {summary?.actionItems.map((item, index) => (
            <li key={index} className="flex items-start gap-3 bg-orange-50 rounded-lg p-3 border border-orange-100">
              <i className="ri-arrow-right-circle-fill text-orange-500 text-lg flex-shrink-0 mt-0.5"></i>
              <span className="text-slate-700 text-sm leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Highlights */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
            <i className="ri-star-fill text-white text-sm"></i>
          </div>
          <h3 className="text-lg font-semibold text-slate-900">Highlights</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {summary?.highlights.map((highlight, index) => (
            <div key={index} className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-4 border border-yellow-200">
              <div className="flex items-start gap-2">
                <i className="ri-flashlight-fill text-yellow-600 text-lg flex-shrink-0"></i>
                <span className="text-slate-700 text-sm leading-relaxed">{highlight}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Export Button */}
      <div className="pt-4 border-t border-slate-200">
        <button className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg font-medium hover:from-purple-600 hover:to-blue-600 transition-all whitespace-nowrap flex items-center justify-center gap-2">
          <i className="ri-download-2-line"></i>
          Export Summary
        </button>
      </div>
    </div>
  );
};

export default AISummaryPanel;